"use strict"

import React from "react"
import memoize from "memoize-one"

import { zoomGraphsEqual } from "../graphing/resources/utils"

const run_accessor = "runID"

const ConfigContext = React.createContext({})

export function withZoomState(Component) {
  return function (props) {
    return (
      <ConfigContext.Consumer>
        {(graphState) => <Component {...props} {...graphState} />}
      </ConfigContext.Consumer>
    )
  }
}

export default class ZoomConfigProvider extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      /*
       * Array of {graph, ydomain, ?y2domain}.
       *
       * We currently test for deepEquals of various graphing features, which isn't ideal from
       * a performance standpoint, but will have to do unless we come up with some sort
       * of global identity for graphs. (TBD)
       */
      perGraphZooms: [],

      perRunTimeOffsets: {},

      zooming: false,
    }

    this.updateTimeOffset = this.updateTimeOffset.bind(this)
  }

  render() {
    return (
      <ConfigContext.Provider
        value={{
          minTime: this.state.minTime,
          maxTime: this.state.maxTime,
          zoomConfig: this.wrapZooms(
            this.state.perGraphZooms,
            this.state.minTime,
            this.state.maxTime,
            this.state.zooming,
          ),

          perRunTimeOffsets: this.state.perRunTimeOffsets,
          updateTimeOffset: this.updateTimeOffset,
          run_accessor: run_accessor,
        }}
      >
        {this.props.children}
      </ConfigContext.Provider>
    )
  }

  wrapZooms = memoize(
    (perGraphZooms, minTime, maxTime, zooming) =>
      new ZoomHolder(this, perGraphZooms, minTime, maxTime, zooming),
  )

  updateTimeOffset(id, offset) {
    this.setState((state) => ({
      perRunTimeOffsets: {
        ...state.perRunTimeOffsets,
        [id]: offset,
      },
    }))
  }
}

/**
 * Helper which is used to expose the per-graph zoom data to dashboards, and provide
 * a sensible "command" interface for mutating the zoom state.
 */

class ZoomHolder {
  constructor(provider, perGraphZooms, minTime, maxTime, zooming) {
    this.provider = provider
    this.minTime = minTime
    this.maxTime = maxTime
    this.zooming = zooming

    // Note that these are constructed ahead of time (rather than on-demand),
    // to ensure object identity across re-renders (unless something changes...)
    this.perGraphZooms = (perGraphZooms || []).map((record) => ({
      ...record,
      time: [this.minTime, this.maxTime],
    }))

    this.defaultZoom = {
      time: [this.minTime, this.maxTime],
    }
  }

  isZoomMode() {
    return this.zooming
  }

  setZoomMode(zoomMode) {
    this.provider.setState({
      zooming: zoomMode,
    })
  }

  toggleZoomMode() {
    this.provider.setState((state) => ({
      zooming: !state.zooming,
    }))
  }

  /**
   * Find a zoom configuration that best matches the specified plot.
   */

  zoomForPlot(id, plotConfig) {
    if (!plotConfig) return this.defaultZoom
    let runSpecific = false,
      match = null
    for (const zoom of this.perGraphZooms) {
      if (zoomGraphsEqual(zoom.graph, plotConfig)) {
        if (zoom[run_accessor] === undefined) {
          if (!runSpecific) {
            match = zoom
          }
        } else if (zoom[run_accessor] === id) {
          match = zoom
          runSpecific = true
        }
      }
    }

    return match || this.defaultZoom
  }

  plotsAreZoomed() {
    return this.minTime !== undefined || this.maxTime !== undefined || this.perGraphZooms.length > 0
  }

  /**
   * Set a new constraint for the time axis.
   */

  zoomTime(minTime, maxTime) {
    this.provider.setState({
      minTime,
      maxTime,
    })
  }

  /**
   * Remove all constraints on the time axis
   */

  unzoomTime() {
    this.provider.setState({
      minTime: undefined,
      maxTime: undefined,
    })
  }

  /**
   * Mark a run/plot combination as a candidate for "independent zooming",
   * i.e. subsequent zoomRun commands will apply only to this plot.
   */

  zoomRunIndependently(graphKey, id) {
    this.provider.setState((state) => {
      const prevZoomIndex = findZoomForRunPlot(state.perGraphZooms, graphKey, id)
      if (prevZoomIndex < 0) {
        return {
          perGraphZooms: [...state.perGraphZooms, { graph: graphKey, [run_accessor]: id }],
        }
      } else {
        const prev = state.perGraphZooms[prevZoomIndex]
        if (prev[run_accessor] !== undefined) {
          // Already got an independent zoom record.
          return null
        } else {
          return {
            perGraphZooms: [
              ...state.perGraphZooms,
              {
                ...prev,
                [run_accessor]: id,
              },
            ],
          }
        }
      }
    })
  }

  /**
   * Remove any run-specific zoom constraints for a given plot.
   */

  zoomRunCollectively(graphKey, id) {
    this.provider.setState((state) => {
      const prevZoomIndex = findZoomForRunPlot(state.perGraphZooms, graphKey, id)
      if (prevZoomIndex >= 0) {
        const prev = state.perGraphZooms[prevZoomIndex]
        if (prev[run_accessor] !== undefined) {
          const update = [...state.perGraphZooms]
          update.splice(prevZoomIndex, 1)
          return {
            perGraphZooms: update,
          }
        }
      }

      return null
    })
  }

  /**
   * Apply some constraints. id will be ignored unless there's an
   * existing independent zoom record for that graph/run combination.
   */

  zoomPlot(graphKey, id, axisConstraints) {
    this.provider.setState((state) => {
      const prevZoomIndex = findZoomForRunPlot(state.perGraphZooms, graphKey, id)

      const update = [...state.perGraphZooms]
      if (prevZoomIndex >= 0) {
        update[prevZoomIndex] = {
          ...update[prevZoomIndex],
          ...axisConstraints,
        }
      } else {
        update.push({
          graph: graphKey,
          ...axisConstraints,
        })
      }

      return {
        perGraphZooms: update,
      }
    })
  }

  /**
   * Remove all zoom constraints.
   */

  unzoomAllPlots() {
    this.provider.setState({
      perGraphZooms: [],
    })
  }
}

function findZoomForRunPlot(zooms, graphKey, id) {
  let runSpecific = false,
    match = -1

  for (let i = 0; i < zooms.length; ++i) {
    const zoom = zooms[i]
    if (zoomGraphsEqual(zoom.graph, graphKey)) {
      if (zoom[run_accessor] === undefined) {
        if (!runSpecific) {
          match = i
        }
      } else if (zoom[run_accessor] === id) {
        match = i
        runSpecific = true
      }
    }
  }

  return match
}
